import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Trophy, Loader2, ShieldCheck, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function ConsentSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ guardian_name: "", guardian_phone: "", emergency_contact: "", medical_notes: "", agree: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/consent/${token}`).then((r) => {
      setData(r.data);
      if (r.data.consent.status === "consented") setDone(true);
    }).catch(() => setNotFound(true));
  }, [token]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.agree) { toast.error("Please tick the consent checkbox"); return; }
    setLoading(true);
    try {
      await api.post(`/consent/${token}/sign`, form);
      setDone(true);
      toast.success("Consent recorded. Thank you!");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  if (notFound) return <Center><p className="text-muted-foreground">This consent link is invalid or has expired.</p></Center>;
  if (!data) return <Center><Loader2 className="h-6 w-6 animate-spin text-primary" /></Center>;

  const { consent, booking } = data;

  return (
    <div className="grain min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-xl animate-fadeUp">
        <div className="mb-6 flex items-center justify-center gap-2">
          <Trophy className="h-7 w-7 text-primary" />
          <span className="font-display text-3xl font-extrabold tracking-wide">SPORA<span className="text-primary">1</span></span>
        </div>
        <div className="rounded-2xl border border-border bg-card p-8 shadow-2xl">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 font-mono text-xs uppercase tracking-widest text-primary">
            <ShieldCheck className="h-3.5 w-3.5" /> Parental Consent
          </div>
          <h1 className="font-display text-2xl font-bold">Consent for {consent.participant_name}</h1>
          {booking && (
            <div className="mt-4 space-y-2 rounded-xl border border-border bg-background p-4 text-sm">
              <Row label="Program" value={booking.title} />
              <Row label="Type" value={booking.type} />
              <Row label="Sport" value={booking.sport} />
              <Row label="Institution" value={booking.institution_name} />
              {booking.itinerary?.length > 0 && (
                <div className="pt-2">
                  <p className="mb-1 font-mono text-xs uppercase text-muted-foreground">Itinerary</p>
                  {booking.itinerary.map((it, i) => (
                    <p key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" /> {it.day} {it.time} - {it.title} {it.location ? `(${it.location})` : ""}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {done ? (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              <p className="font-semibold text-emerald-300">Consent recorded</p>
              <p className="text-sm text-muted-foreground">Thank you. Your consent for {consent.participant_name} has been saved.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <Field label="Guardian name" testId="consent-guardian-name" value={form.guardian_name} onChange={set("guardian_name")} required />
              <Field label="Guardian phone" testId="consent-guardian-phone" value={form.guardian_phone} onChange={set("guardian_phone")} required />
              <Field label="Emergency contact" testId="consent-emergency-contact" value={form.emergency_contact} onChange={set("emergency_contact")} required />
              <div>
                <label className="mb-1.5 block text-sm font-medium">Medical notes / allergies (optional)</label>
                <textarea data-testid="consent-medical-notes" value={form.medical_notes} onChange={set("medical_notes")} rows={2}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
              </div>
              <label className="flex items-start gap-3 rounded-lg border border-border bg-background p-3">
                <input data-testid="consent-agree-checkbox" type="checkbox" checked={form.agree}
                  onChange={(e) => setForm((f) => ({ ...f, agree: e.target.checked }))} className="mt-1 h-4 w-4 accent-[#FF6B00]" />
                <span className="text-sm text-muted-foreground">
                  I am the parent/guardian and I consent to {consent.participant_name}'s participation in this program,
                  and confirm the emergency and medical information provided is accurate.
                </span>
              </label>
              <button data-testid="consent-sign-btn" type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 font-semibold text-primary-foreground transition-all hover:bg-[#FF8533] disabled:opacity-60">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />} Give consent
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="flex min-h-screen items-center justify-center bg-background px-4 text-center">{children}</div>;
const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4"><span className="text-muted-foreground">{label}</span><span className="font-medium capitalize">{value || "-"}</span></div>
);
const Field = ({ label, testId, ...props }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium">{label}</label>
    <input data-testid={testId} {...props}
      className="w-full rounded-lg border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-primary" />
  </div>
);
